import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Users,
  Shield,
  Key,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  AlertTriangle,
  Eye,
  EyeOff,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { createUser } from '../../../api/admin';
import { listGroups } from '../../../api/group';
import { getAllPermissions } from '../../../api/iam';
import { useToast } from '../../../hooks/useToast';
import { ToastContainer } from '../../../components/ui/Toast';
import Select from '../../../components/ui/Select';

const CreateUserWizard = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Permissions state for better UX
  const [permissionsSearch, setPermissionsSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [groupPermissions, setGroupPermissions] = useState([]); // Permissions from selected groups

  // Toast system
  const { toasts, removeToast, showError, showSuccess, showWarning } = useToast();

  // Form data state
  const [formData, setFormData] = useState({
    // Step 1 - Basic Details
    email: '',
    password: '',
    name: '',
    role: 'Admin',

    // Step 2 - Groups
    selectedGroups: [],

    // Step 3 - Permissions
    selectedPermissions: []
  });

  // Data states
  const [groups, setGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [groupsPagination, setGroupsPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  const steps = [
    { number: 1, title: 'Basic Details', icon: User },
    { number: 2, title: 'Select Groups', icon: Users },
    { number: 3, title: 'Select Permissions', icon: Key }
  ];

  // Load groups and permissions when wizard opens
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Extract permissions from selected groups whenever groups change
  useEffect(() => {
    if (formData.selectedGroups.length === 0) {
      setGroupPermissions([]);
      return;
    }

    // Get all permissions from selected groups' policies
    const permissionsFromGroups = new Set();

    formData.selectedGroups.forEach(groupId => {
      const group = groups.find(g => g._id === groupId);
      if (group && group.policyIds) {
        // policyIds can be populated objects or just IDs
        group.policyIds.forEach(policy => {
          // Check if policy is populated
          const policyObj = policy._id ? policy : null;
          if (policyObj && policyObj.statements) {
            policyObj.statements.forEach(statement => {
              if (statement.effect === 'Allow' && statement.action) {
                statement.action.forEach(action => {
                  // Match action to permission by name
                  const matchingPermission = permissions.find(p => p.name === action);
                  if (matchingPermission) {
                    permissionsFromGroups.add(matchingPermission._id || matchingPermission.id);
                  }
                });
              }
            });
          }
        });
      }
    });

    const groupPermsArray = Array.from(permissionsFromGroups);
    setGroupPermissions(groupPermsArray);

    // Auto-select group permissions in formData
    setFormData(prev => ({
      ...prev,
      selectedPermissions: [...new Set([...groupPermsArray, ...prev.selectedPermissions])]
    }));
  }, [formData.selectedGroups, groups, permissions]);

  const loadData = async () => {
    setDataLoading(true);
    try {
      // Load groups with populated policies
      const groupsResponse = await listGroups({ page: 1, limit: 100 });
      setGroups(groupsResponse.data?.data?.groups || []);

      // Load permissions  
      const permissionsResponse = await getAllPermissions({ page: 1, limit: 1000 });
      setPermissions(permissionsResponse.data?.data?.permissions || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load groups and permissions');
    } finally {
      setDataLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'Admin',
      selectedGroups: [],
      selectedPermissions: []
    });
    setCurrentStep(1);
    setError(null);
    setValidationErrors({});
    setShowPassword(false);
    setGroupPermissions([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };

    return validations;
  };

  const validateStep1 = () => {
    const errors = {};
    let isValid = true;

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
      showError('Email is required');
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
      showError('Please enter a valid email address');
    }

    // Password validation
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
      isValid = false;
      showError('Password is required');
    } else {
      const passwordValidation = validatePassword(formData.password);
      const passwordErrors = [];

      if (!passwordValidation.length) passwordErrors.push('at least 8 characters');
      if (!passwordValidation.lowercase) passwordErrors.push('a lowercase letter');
      if (!passwordValidation.uppercase) passwordErrors.push('an uppercase letter');
      if (!passwordValidation.number) passwordErrors.push('a number');
      if (!passwordValidation.special) passwordErrors.push('a special character');

      if (passwordErrors.length > 0) {
        errors.password = `Password must contain ${passwordErrors.join(', ')}`;
        isValid = false;
        showError(`Password must contain ${passwordErrors.join(', ')}`);
      }
    }

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
      isValid = false;
      showError('Full name is required');
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters long';
      isValid = false;
      showError('Name must be at least 2 characters long');
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Check if administration group is selected
  const isAdministrationGroupSelected = () => {
    return formData.selectedGroups.some(groupId => {
      const group = groups.find(g => g._id === groupId);
      return group && group.name.toLowerCase().includes('admin');
    });
  };

  // Group permissions by category/module
  const groupPermissionsByCategory = (permissions) => {
    const categories = {};
    permissions.forEach(permission => {
      const parts = permission.name.split(':');
      const category = parts[0] || 'Other';

      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(permission);
    });
    return categories;
  };

  // Filter permissions by search term
  const filterPermissions = (permissions) => {
    if (!permissionsSearch.trim()) return permissions;

    return permissions.filter(permission =>
      permission.name.toLowerCase().includes(permissionsSearch.toLowerCase()) ||
      (permission.description && permission.description.toLowerCase().includes(permissionsSearch.toLowerCase()))
    );
  };

  // Toggle category expansion
  const toggleCategory = (categoryName) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  // Select all permissions in a category
  const selectAllInCategory = (categoryPermissions) => {
    const categoryIds = categoryPermissions.map(p => p._id || p.id);
    // Only consider non-group permissions for toggle logic
    const nonGroupCategoryIds = categoryIds.filter(id => !groupPermissions.includes(id));
    const allNonGroupSelected = nonGroupCategoryIds.every(id => formData.selectedPermissions.includes(id));

    if (allNonGroupSelected) {
      // Deselect only non-group permissions in category
      setFormData(prev => ({
        ...prev,
        selectedPermissions: prev.selectedPermissions.filter(id => !nonGroupCategoryIds.includes(id))
      }));
    } else {
      // Select all permissions in category (including group ones that are already selected)
      setFormData(prev => ({
        ...prev,
        selectedPermissions: [...new Set([...prev.selectedPermissions, ...categoryIds])]
      }));
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const isValid = validateStep1();
      if (!isValid) {
        return; // Block progression if validation fails
      }
      showSuccess('Step 1 completed successfully!', 2000);
    }

    setError(null);
    setValidationErrors({});
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevious = () => {
    setError(null);
    setValidationErrors({});
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleGroupToggle = (groupId) => {
    setFormData(prev => ({
      ...prev,
      selectedGroups: prev.selectedGroups.includes(groupId)
        ? prev.selectedGroups.filter(id => id !== groupId)
        : [...prev.selectedGroups, groupId]
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    // Prevent deselecting permissions from groups
    if (groupPermissions.includes(permissionId)) {
      showWarning('Cannot deselect permissions inherited from groups');
      return;
    }

    setFormData(prev => ({
      ...prev,
      selectedPermissions: prev.selectedPermissions.includes(permissionId)
        ? prev.selectedPermissions.filter(id => id !== permissionId)
        : [...prev.selectedPermissions, permissionId]
    }));
  };

  const handleSubmit = async () => {
    // Final validation before submission
    const isValidStep1 = validateStep1();
    if (!isValidStep1) {
      showError('Please fix the validation errors in Step 1');
      setCurrentStep(1);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        role: formData.role,
        name: formData.name,
        groups: formData.selectedGroups,
        permissions: formData.selectedPermissions
      };

      const response = await createUser(payload);
      console.log('User created successfully:', response);

      showSuccess('User created successfully!', 3000);

      // Delay closing to show success message
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1000);

    } catch (err) {
      console.error('Failed to create user:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create user';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" style={{ fontFamily: 'Poppins' }}>
        <div className="relative top-10 mx-auto border-2 w-11/12 max-w-4xl shadow-xl rounded-xl bg-white">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b-2 border-gray-200">
            <div className="flex items-center">
              <User className="w-8 h-8 text-[#2D869C] mr-3" />
              <h3 className="text-2xl font-bold text-gray-900">Create New User</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="px-6 py-5 border-b-2 border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.number;
                const isCompleted = currentStep > step.number;

                return (
                  <div key={step.number} className="flex items-center">
                    <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${isCompleted
                      ? 'bg-green-100 border-green-500 text-green-600'
                      : isActive
                        ? 'bg-[#2D869C]/10 border-[#2D869C] text-[#2D869C]'
                        : 'bg-gray-100 border-gray-300 text-gray-400'
                      }`}>
                      {isCompleted ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className={`text-base font-semibold ${isActive ? 'text-[#2D869C]' : isCompleted ? 'text-green-600' : 'text-gray-500'
                        }`}>
                        Step {step.number}
                      </p>
                      <p className={`text-sm font-medium ${isActive ? 'text-[#2D869C]/80' : isCompleted ? 'text-green-500' : 'text-gray-400'
                        }`}>
                        {step.title}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-px mx-4 ${isCompleted ? 'bg-green-300' : 'bg-gray-300'
                        }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border-l-4 border-red-400">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
                <div className="text-sm text-red-800">{error}</div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {dataLoading && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading data...</span>
            </div>
          )}

          {/* Step Content */}
          {!dataLoading && (
            <div className="p-6 min-h-[400px]">
              {/* Step 1: Basic Details */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-5">Basic User Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          autoComplete="off"
                          onChange={(e) => {
                            const newEmail = e.target.value;
                            setFormData({ ...formData, email: newEmail });

                            // Real-time validation
                            if (newEmail && !validateEmail(newEmail)) {
                              setValidationErrors(prev => ({ ...prev, email: 'Invalid email format' }));
                            } else {
                              setValidationErrors(prev => ({ ...prev, email: null }));
                            }
                          }}
                          className={`w-full px-4 py-2.5 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] ${validationErrors.email ? 'border-red-300 bg-red-50' : formData.email && validateEmail(formData.email) ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                          placeholder="user@hospital.com"
                        />
                        {validationErrors.email && (
                          <p className="mt-1 text-xs text-red-600 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {validationErrors.email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          autoComplete="off"
                          onChange={(e) => {
                            setFormData({ ...formData, name: e.target.value });
                            // Clear name error when user starts typing
                            if (validationErrors.name) {
                              setValidationErrors(prev => ({ ...prev, name: null }));
                            }
                          }}
                          className={`w-full px-4 py-2.5 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] ${validationErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                          placeholder="John Doe"
                        />
                        {validationErrors.name && (
                          <p className="mt-1 text-xs text-red-600 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {validationErrors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-2">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            autoComplete="new-password"
                            onChange={(e) => {
                              setFormData({ ...formData, password: e.target.value });
                              // Clear password error when user starts typing
                              if (validationErrors.password) {
                                setValidationErrors(prev => ({ ...prev, password: null }));
                              }
                            }}
                            className={`w-full px-4 py-2.5 pr-12 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] ${validationErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              }`}
                            placeholder="Minimum 8 characters"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>

                        {/* Password Strength Indicator */}
                        {formData.password && (
                          <div className="mt-2 space-y-1">
                            {(() => {
                              const validations = validatePassword(formData.password);
                              return (
                                <div className="space-y-1">
                                  <div className={`text-xs flex items-center ${validations.length ? 'text-green-600' : 'text-red-600'}`}>
                                    {validations.length ? '✓' : '✗'} At least 8 characters
                                  </div>
                                  <div className={`text-xs flex items-center ${validations.lowercase ? 'text-green-600' : 'text-red-600'}`}>
                                    {validations.lowercase ? '✓' : '✗'} One lowercase letter
                                  </div>
                                  <div className={`text-xs flex items-center ${validations.uppercase ? 'text-green-600' : 'text-red-600'}`}>
                                    {validations.uppercase ? '✓' : '✗'} One uppercase letter
                                  </div>
                                  <div className={`text-xs flex items-center ${validations.number ? 'text-green-600' : 'text-red-600'}`}>
                                    {validations.number ? '✓' : '✗'} One number
                                  </div>
                                  <div className={`text-xs flex items-center ${validations.special ? 'text-green-600' : 'text-red-600'}`}>
                                    {validations.special ? '✓' : '✗'} One special character
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {validationErrors.password && (
                          <p className="mt-1 text-xs text-red-600 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {validationErrors.password}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-2">
                          Role <span className="text-red-500">*</span>
                        </label>
                        <Select
                          value={formData.role}
                          onChange={(value) => setFormData({ ...formData, role: value })}
                          options={[
                            { value: "Admin", label: "Admin" },
                            { value: "Doctor", label: "Doctor" },
                            { value: "Receptionist", label: "Receptionist" },
                            { value: "Nurse", label: "Nurse" }
                          ]}
                          placeholder="Select a role"
                          required
                          className="[&>div>button]:h-auto [&>div>button]:px-4 [&>div>button]:py-2.5 [&>div>button]:text-lg [&>div>button]:border [&>div>button]:border-gray-300 [&>div>button]:rounded-lg [&>div>button]:focus:ring-2 [&>div>button]:focus:ring-[#2D869C] [&>div>button]:focus:border-[#2D869C]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Select Groups */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-3">Select Groups</h4>
                    <p className="text-lg font-medium text-gray-600 mb-5">
                      Choose which groups this user should belong to. Groups provide organized permission sets.
                    </p>

                    <div className="border-2 border-gray-200 rounded-xl max-h-96 overflow-y-auto shadow-sm">
                      {groups.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                          {groups.map((group) => (
                            <div key={group._id} className="p-4 hover:bg-gray-50">
                              <label className="flex items-start cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.selectedGroups.includes(group._id)}
                                  onChange={() => handleGroupToggle(group._id)}
                                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <div className="ml-3 flex-1">
                                  <div className="flex items-center">
                                    <Shield className="w-5 h-5 text-[#2D869C] mr-2" />
                                    <p className="text-lg font-semibold text-gray-900">{group.name}</p>
                                  </div>
                                  <p className="text-sm font-medium text-gray-500 mt-1">
                                    {group.description || 'No description available'}
                                  </p>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center">
                          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">No groups available</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 space-y-3">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Selected:</strong> {formData.selectedGroups.length} group(s)
                        </p>
                      </div>

                      {/* Show administration group info */}
                      {isAdministrationGroupSelected() && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center">
                            <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                            <div className="text-sm text-green-800">
                              <strong>Administration Group Selected!</strong>
                              <p className="mt-1">You can create the user directly as administrators have full permissions by default.</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Select Permissions */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-3">Select Permissions</h4>
                    <p className="text-lg font-medium text-gray-600 mb-5">
                      Choose individual permissions for this user. These will be added to any permissions inherited from groups.
                    </p>

                    {/* Search Bar */}
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search permissions..."
                          value={permissionsSearch}
                          onChange={(e) => setPermissionsSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C]"
                        />
                      </div>
                    </div>

                    {permissions.length > 0 ? (
                      (() => {
                        const filteredPermissions = filterPermissions(permissions);
                        const categorizedPermissions = groupPermissionsByCategory(filteredPermissions);
                        const categoryNames = Object.keys(categorizedPermissions).sort();

                        return (
                          <div className="border-2 border-gray-200 rounded-xl max-h-96 overflow-y-auto shadow-sm">
                            {categoryNames.length > 0 ? (
                              <div className="divide-y divide-gray-200">
                                {categoryNames.map((categoryName) => {
                                  const categoryPermissions = categorizedPermissions[categoryName];
                                  const isExpanded = expandedCategories.has(categoryName);
                                  const selectedInCategory = categoryPermissions.filter(p =>
                                    formData.selectedPermissions.includes(p._id || p.id)
                                  ).length;

                                  return (
                                    <div key={categoryName}>
                                      {/* Category Header */}
                                      <div className="p-4 bg-gray-50 border-b border-gray-200">
                                        <div className="flex items-center justify-between">
                                          <button
                                            onClick={() => toggleCategory(categoryName)}
                                            className="flex items-center space-x-2 text-left hover:text-blue-600"
                                          >
                                            {isExpanded ? (
                                              <ChevronUp className="w-4 h-4" />
                                            ) : (
                                              <ChevronDown className="w-4 h-4" />
                                            )}
                                            <Shield className="w-4 h-4 text-blue-600" />
                                            <div>
                                              <p className="font-medium text-gray-900 capitalize">
                                                {categoryName.replace(/([A-Z])/g, ' $1').trim()}
                                              </p>
                                              <p className="text-xs text-gray-500">
                                                {selectedInCategory}/{categoryPermissions.length} selected
                                              </p>
                                            </div>
                                          </button>

                                          <div className="flex items-center space-x-2">
                                            <button
                                              onClick={() => selectAllInCategory(categoryPermissions)}
                                              className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50"
                                            >
                                              {selectedInCategory === categoryPermissions.length ? 'Deselect All' : 'Select All'}
                                            </button>
                                            <span className="text-xs text-gray-500">
                                              ({categoryPermissions.length})
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Category Permissions */}
                                      {isExpanded && (
                                        <div className="divide-y divide-gray-100">
                                          {categoryPermissions.map((permission) => {
                                            const permId = permission._id || permission.id;
                                            const isSelected = formData.selectedPermissions.includes(permId);
                                            const isFromGroup = groupPermissions.includes(permId);

                                            return (
                                              <div
                                                key={permId}
                                                className={`p-3 hover:bg-gray-50 ${isFromGroup ? 'bg-green-50' : ''
                                                  }`}
                                              >
                                                <label className="flex items-start cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    disabled={isFromGroup}
                                                    onChange={() => handlePermissionToggle(permId)}
                                                    className={`mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isFromGroup ? 'opacity-60 cursor-not-allowed' : ''
                                                      }`}
                                                  />
                                                  <div className="ml-3 flex-1">
                                                    <div className="flex items-center gap-2">
                                                      <Key className="w-3 h-3 text-orange-600 mr-2" />
                                                      <p className="text-sm font-medium text-gray-900">{permission.name}</p>
                                                      {isFromGroup && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                          <Shield className="w-3 h-3 mr-1" />
                                                          Default From Group
                                                        </span>
                                                      )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                      {permission.description || 'No description available'}
                                                    </p>
                                                  </div>
                                                </label>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="p-8 text-center">
                                <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">No permissions match your search</p>
                                <p className="text-sm text-gray-400 mt-1">Try different keywords</p>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="border border-gray-200 rounded-lg p-8 text-center">
                        <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No permissions available</p>
                      </div>
                    )}

                    <div className="mt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="p-3 bg-blue-50 rounded-lg flex-1 mr-4">
                          <p className="text-sm text-blue-800">
                            <strong>Selected:</strong> {formData.selectedPermissions.length} permission(s)
                          </p>
                        </div>

                        {permissions.length > 0 && (
                          <button
                            onClick={() => {
                              // Expand all categories for better overview
                              const allCategories = Object.keys(groupPermissionsByCategory(permissions));
                              setExpandedCategories(new Set(allCategories));
                            }}
                            className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Expand All
                          </button>
                        )}
                      </div>

                      {groupPermissions.length > 0 && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-start">
                            <Shield className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-green-800">
                              <strong>{groupPermissions.length} permission(s) inherited from selected groups</strong>
                              <p className="mt-1 text-xs">These permissions are automatically selected and cannot be removed. You can add more permissions below.</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 hidden">

                      {permissions.length > 0 && (
                        <button
                          onClick={() => {
                            // Expand all categories for better overview
                            const allCategories = Object.keys(groupPermissionsByCategory(permissions));
                            setExpandedCategories(new Set(allCategories));
                          }}
                          className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Expand All
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-5 border-t-2 border-gray-200 bg-gray-50">
            <div className="flex space-x-3">
              {currentStep > 1 && (
                <button
                  onClick={handlePrevious}
                  className="inline-flex items-center px-5 py-2.5 border-2 border-gray-300 rounded-lg text-lg font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Previous
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-lg font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>

              {currentStep < 3 ? (
                // Show Create User button if in step 2 and administration group is selected
                currentStep === 2 && isAdministrationGroupSelected() ? (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="inline-flex items-center px-5 py-2.5 bg-[#2D869C] text-white text-lg font-semibold rounded-lg hover:bg-[#247180] disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Create User
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="inline-flex items-center px-5 py-2.5 bg-[#2D869C] text-white text-lg font-semibold rounded-lg hover:bg-[#247180] transition-colors shadow-sm"
                  >
                    Next
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </button>
                )
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="inline-flex items-center px-5 py-2.5 bg-[#2D869C] text-white text-lg font-semibold rounded-lg hover:bg-[#247180] disabled:opacity-50 transition-colors shadow-sm"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Create User
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateUserWizard;
